<template>
    <v-menu
        v-model="menu"
        :close-on-content-click="false"
        location="bottom start"
        :offset="6"
        :disabled="isLocked"
        content-class="bs-date-pop"
    >
        <template v-slot:activator="{ props: activator }">
            <v-text-field
                placeholder="DD/MM/YYYY"
                append-inner-icon="mdi-calendar"
                v-bind="{ ...$attrs, ...activator }"
                :model-value="displayValue"
                :validation-value="modelValue"
                readonly
                role="combobox"
                aria-haspopup="dialog"
                :aria-expanded="menu ? 'true' : 'false'"
                :aria-activedescendant="menu && focusIso ? cellId(focusIso) : undefined"
                @keydown="onFieldKeydown"
                @click:clear="clearDate"
            >
                <template v-for="(_, name) in $slots" #[name]="slotProps">
                    <slot :name="name" v-bind="slotProps || {}"></slot>
                </template>
            </v-text-field>
        </template>
        <!-- mousedown.prevent keeps focus in the field: the panel is driven by the
             field's keyboard handler, and a blur would fire the caller's @blur
             (some tables save on it) before a day had been picked. -->
        <v-sheet class="bs-cal" role="dialog" :aria-label="'Choose date, ' + monthLabel" @mousedown.prevent>
            <div class="bs-cal__hd">
                <button type="button" class="bs-cal__nav" aria-label="Previous month" tabindex="-1" @click="shiftMonth(-1)">
                    <v-icon size="18">mdi-chevron-left</v-icon>
                </button>
                <b aria-live="polite">{{ monthLabel }}</b>
                <button type="button" class="bs-cal__nav" aria-label="Next month" tabindex="-1" @click="shiftMonth(1)">
                    <v-icon size="18">mdi-chevron-right</v-icon>
                </button>
            </div>
            <div class="bs-cal__grid" role="grid" :aria-label="monthLabel">
                <div v-for="wd in weekdays" :key="wd" class="bs-cal__wd" role="columnheader">{{ wd }}</div>
                <button
                    v-for="d in cells"
                    :id="cellId(d.iso)"
                    :key="d.iso"
                    type="button"
                    tabindex="-1"
                    role="gridcell"
                    class="bs-cal__d"
                    :class="{ out: d.out, today: d.iso === todayIso, sel: d.iso === selectedIso, foc: d.iso === focusIso }"
                    :disabled="!d.allowed"
                    :aria-selected="d.iso === selectedIso ? 'true' : 'false'"
                    :aria-current="d.iso === todayIso ? 'date' : undefined"
                    :aria-label="d.label"
                    @click="pick(d.iso)"
                >
                    {{ d.day }}
                </button>
            </div>
            <div class="bs-cal__ft">
                <button type="button" class="bs-cal__link" tabindex="-1" :disabled="!isAllowed(todayIso)" @click="pick(todayIso)">Today</button>
                <button
                    v-if="!hideClear && selectedIso"
                    type="button"
                    class="bs-cal__link bs-cal__link--quiet"
                    tabindex="-1"
                    @click="clearDate"
                >
                    Clear
                </button>
            </div>
        </v-sheet>
    </v-menu>
</template>

<script>
import moment from 'moment';

const ISO = 'YYYY-MM-DD';
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
let uid = 0;

/** Anything a caller may bind (Date, 'YYYY-MM-DD', ISO datetime, 'DD/MM/YYYY') -> moment, or null. */
function toMoment(v) {
    if (v === null || v === undefined || v === '') return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : moment(v);
    if (moment.isMoment(v)) return v.isValid() ? v.clone() : null;
    const strict = moment(v, [ISO, moment.ISO_8601, 'DD/MM/YYYY'], true);
    if (strict.isValid()) return strict;
    const loose = moment(new Date(v));
    return loose.isValid() ? loose : null;
}

function toIso(v) {
    const m = toMoment(v);
    return m ? m.format(ISO) : null;
}

/**
 * BsDateField — the design-system date picker (DS v3.0.0 / v1.1 "date field").
 *
 * A read-only field that ALWAYS shows DD/MM/YYYY, opening a month calendar in the
 * shared white menu panel: weeks start on Monday, today has a blue ring, the
 * chosen day is filled blue, disallowed days are greyed and struck through, with
 * Today and Clear. Never the browser's own date picker.
 *
 * Drop-in replacement for <v-text-field type="date" v-model="x" ...>:
 *   <BsDateField v-model="x" label="..." ... />
 * All extra attrs (label, density, hide-details, error-messages, variant,
 * clearable, rules, disabled, readonly, class, listeners ...) and slots pass
 * through to the inner field. Rules see the model value, not the display text.
 *
 * Value contract:
 *   value-type="iso"  (default) emits 'YYYY-MM-DD'
 *   value-type="date" emits a Date at local midnight (for callers that used
 *                     @vuepic/vue-datepicker or a Date-bound v-date-picker)
 *   Clear emits `empty-value` (default null; pass empty-value="" where the
 *   field used to be a native date input, which cleared to '').
 *
 * Which days can be picked:
 *   min / max            'YYYY-MM-DD' or Date, inclusive
 *   allowed-dates        fn(Date) => boolean, or an array of 'YYYY-MM-DD' (Vuetify's name)
 *   disabled-dates       fn(Date) => boolean, or an array of 'YYYY-MM-DD' (vue-datepicker's name)
 *   disabled-week-days   array of weekday numbers, 0 = Sunday
 *
 * Keyboard (focus stays in the field): Enter / Space / Down open; Left/Right a
 * day, Up/Down a week, Page Up / Page Down a month, Home / End the week's ends,
 * Enter picks, Esc closes (VMenu), Tab closes.
 */
export default {
    name: 'BsDateField',
    inheritAttrs: false,
    props: {
        modelValue: { type: [String, Date], default: null },
        min: { type: [String, Date], default: undefined },
        max: { type: [String, Date], default: undefined },
        allowedDates: { type: [Function, Array], default: undefined },
        disabledDates: { type: [Function, Array], default: undefined },
        disabledWeekDays: { type: Array, default: undefined },
        valueType: { type: String, default: 'iso', validator: (v) => ['iso', 'date'].includes(v) },
        emptyValue: { type: null, default: null },
        hideClear: { type: Boolean, default: false }
    },
    emits: ['update:modelValue'],
    data() {
        return {
            menu: false,
            viewYear: new Date().getFullYear(),
            viewMonth: new Date().getMonth(),
            focusIso: null,
            todayIso: moment().format(ISO),
            uid: ++uid
        };
    },
    computed: {
        isLocked() {
            const flag = (v) => v === '' || v === true || v === 'true' || v === 'readonly' || v === 'disabled';
            return flag(this.$attrs.disabled) || flag(this.$attrs.readonly);
        },
        selectedIso() {
            return toIso(this.modelValue);
        },
        displayValue() {
            const m = toMoment(this.modelValue);
            return m ? m.format('DD/MM/YYYY') : '';
        },
        minIso() {
            return toIso(this.min);
        },
        maxIso() {
            return toIso(this.max);
        },
        weekdays() {
            return WEEKDAYS;
        },
        monthLabel() {
            return moment({ y: this.viewYear, M: this.viewMonth, d: 1 }).format('MMMM YYYY');
        },
        cells() {
            const first = moment({ y: this.viewYear, M: this.viewMonth, d: 1 });
            // isoWeekday: Monday = 1 ... Sunday = 7, so weeks start on Monday.
            const start = first.clone().subtract(first.isoWeekday() - 1, 'days');
            const out = [];
            for (let i = 0; i < 42; i++) {
                const m = start.clone().add(i, 'days');
                const iso = m.format(ISO);
                out.push({
                    iso,
                    day: m.date(),
                    out: m.month() !== this.viewMonth,
                    allowed: this.isAllowed(iso),
                    label: m.format('dddd D MMMM YYYY')
                });
            }
            return out;
        }
    },
    watch: {
        menu(open) {
            if (open) {
                // Re-read today on every open so a page left open overnight is right.
                this.todayIso = moment().format(ISO);
                this.setFocus(this.selectedIso || this.todayIso);
            }
        }
    },
    methods: {
        cellId(iso) {
            return `bs-date-${this.uid}-${iso}`;
        },
        setFocus(iso) {
            const m = moment(iso, ISO);
            this.focusIso = iso;
            this.viewYear = m.year();
            this.viewMonth = m.month();
        },
        shiftMonth(delta) {
            const m = moment({ y: this.viewYear, M: this.viewMonth, d: 1 }).add(delta, 'months');
            this.viewYear = m.year();
            this.viewMonth = m.month();
            if (this.focusIso) {
                this.focusIso = moment(this.focusIso, ISO).add(delta, 'months').format(ISO);
            }
        },
        isAllowed(iso) {
            if (!iso) return false;
            if (this.minIso && iso < this.minIso) return false;
            if (this.maxIso && iso > this.maxIso) return false;
            const date = moment(iso, ISO).toDate();
            if (Array.isArray(this.disabledWeekDays) && this.disabledWeekDays.map(Number).includes(date.getDay())) return false;
            if (typeof this.allowedDates === 'function' && !this.allowedDates(date)) return false;
            if (Array.isArray(this.allowedDates) && !this.allowedDates.map(toIso).includes(iso)) return false;
            if (typeof this.disabledDates === 'function' && this.disabledDates(date)) return false;
            if (Array.isArray(this.disabledDates) && this.disabledDates.map(toIso).includes(iso)) return false;
            return true;
        },
        pick(iso) {
            if (!this.isAllowed(iso)) return;
            const value = this.valueType === 'date' ? moment(iso, ISO).toDate() : iso;
            this.$emit('update:modelValue', value);
            this.menu = false;
        },
        clearDate() {
            this.$emit('update:modelValue', this.emptyValue);
            this.menu = false;
        },
        onFieldKeydown(e) {
            if (this.isLocked) return;
            const key = e.key;
            if (!this.menu) {
                if (key === 'Enter' || key === ' ' || key === 'ArrowDown') {
                    e.preventDefault();
                    this.menu = true;
                }
                return;
            }
            if (key === 'Tab') {
                this.menu = false;
                return;
            }
            const steps = {
                ArrowLeft: [-1, 'days'],
                ArrowRight: [1, 'days'],
                ArrowUp: [-7, 'days'],
                ArrowDown: [7, 'days'],
                PageUp: [-1, 'months'],
                PageDown: [1, 'months']
            };
            const f = moment(this.focusIso || this.todayIso, ISO);
            if (steps[key]) {
                e.preventDefault();
                this.setFocus(f.add(steps[key][0], steps[key][1]).format(ISO));
            } else if (key === 'Home') {
                e.preventDefault();
                this.setFocus(f.subtract(f.isoWeekday() - 1, 'days').format(ISO));
            } else if (key === 'End') {
                e.preventDefault();
                this.setFocus(f.add(7 - f.isoWeekday(), 'days').format(ISO));
            } else if (key === 'Enter' || key === ' ') {
                e.preventDefault();
                this.pick(this.focusIso);
            }
        }
    }
};
</script>

<!-- Unscoped: the panel is teleported by VMenu, outside this component's scope.
     The frame (white, border, radius, shadow) comes from _ds-menu.scss, which
     dresses any v-sheet that is the direct child of a menu. -->
<style lang="scss">
.bs-date-pop > .bs-cal {
    width: 300px;
    max-width: calc(100vw - 24px);
    padding: var(--bs-space-3);
    font-family: var(--bs-font);
}

.bs-cal__hd {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--bs-space-2);
    margin-bottom: var(--bs-space-2);

    b {
        font-size: 14px;
        font-weight: 600;
    }
}

.bs-cal__nav {
    display: inline-grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: var(--bs-radius-md);
    background: none;
    color: inherit;
    cursor: pointer;
}

.bs-cal__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 2px;
}

.bs-cal__wd {
    height: 28px;
    display: grid;
    place-items: center;
    font-size: 11px;
    font-weight: 600;
    opacity: 0.6;
}

.bs-cal__d {
    height: 36px;
    border: 0;
    border-radius: var(--bs-radius-md);
    background: none;
    color: inherit;
    font: 500 13px/1 var(--bs-font);
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    transition: background var(--dur-fast) var(--bs-ease);

    &.out {
        opacity: 0.5;
    }

    &.today {
        box-shadow: inset 0 0 0 1px rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-primary));
        font-weight: 600;
    }

    &.foc {
        outline: 2px solid rgb(var(--v-theme-primary));
        outline-offset: -2px;
    }

    &.sel,
    &.sel:hover {
        background: rgb(var(--v-theme-primary));
        color: rgb(var(--v-theme-on-primary));
        font-weight: 600;
        box-shadow: none;
        opacity: 1;
    }

    &:disabled {
        background: none;
        cursor: not-allowed;
        text-decoration: line-through;
        opacity: 0.4;
    }
}

.bs-cal__ft {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: var(--bs-space-2);
    padding-top: var(--bs-space-2);
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.bs-cal__link {
    border: 0;
    background: none;
    padding: 6px 8px;
    margin: 0 -8px;
    border-radius: var(--bs-radius-sm);
    font: 600 13px/1 var(--bs-font);
    color: rgb(var(--v-theme-primary));
    cursor: pointer;

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
}

// Light themes take the design-system tokens; DARK_* keep the theme-relative values above.
.bs-cal:not([class*='DARK_']) {
    color: var(--bs-text);

    .bs-cal__nav {
        color: var(--bs-text-secondary);

        &:hover {
            background: var(--bs-border-subtle);
            color: var(--bs-text);
        }
    }

    .bs-cal__wd {
        opacity: 1;
        color: var(--bs-text-muted);
    }

    .bs-cal__d {
        color: var(--bs-text);

        &:hover {
            background: var(--bs-border-subtle);
        }

        &.out {
            opacity: 1;
            color: var(--bs-text-muted);
        }

        &.today {
            box-shadow: inset 0 0 0 1px var(--bs-primary);
            color: var(--bs-primary-pressed);
        }

        &.foc {
            outline-color: var(--bs-primary);
        }

        &.sel,
        &.sel:hover {
            background: var(--bs-primary);
            color: var(--bs-surface);
        }

        &:disabled {
            opacity: 1;
            background: none;
            color: var(--bs-border-strong);
        }
    }

    .bs-cal__ft {
        border-top-color: var(--bs-border-subtle);
    }

    .bs-cal__link {
        color: var(--bs-primary);

        &:hover {
            background: var(--bs-primary-50);
        }

        &--quiet {
            color: var(--bs-text-secondary);

            &:hover {
                background: var(--bs-border-subtle);
            }
        }

        &:disabled {
            opacity: 1;
            background: none;
            color: var(--bs-text-muted);
        }
    }
}

@media (prefers-reduced-motion: reduce) {
    .bs-cal__d {
        transition: none;
    }
}
</style>
